/**
 * Secure Token Storage Service
 * 
 * Provides secure storage for authentication tokens using Capacitor Preferences
 * with encryption for native platforms and secure fallbacks for web.
 */

import { Preferences } from '@capacitor/preferences';
import { isNativeApp } from '@/utils/platformUtils';

/**
 * Token storage configuration
 */
export interface TokenStorageConfig {
  // Encryption settings
  enableEncryption: boolean;
  encryptionAlgorithm: 'AES-GCM' | 'AES-CBC';
  keyDerivationIterations: number;
  
  // Storage settings
  keyPrefix: string;
  tokenExpirationBuffer: number; // milliseconds before expiration to refresh
  
  // Security settings
  enableAutoCleanup: boolean;
  maxTokenAge: number; // milliseconds
  enableTokenValidation: boolean;
  
  // Fallback settings
  enableWebFallback: boolean;
  webStorageType: 'localStorage' | 'sessionStorage';
  
  // Debug settings
  enableDebugLogging: boolean;
}

/**
 * Stored token data structure
 */
export interface StoredTokenData {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  tokenType: string;
  scope?: string;
  userId?: string;
  createdAt: string;
  lastRefreshed?: string;
}

/**
 * Token validation result
 */
export interface TokenValidationResult {
  isValid: boolean;
  isExpired: boolean;
  needsRefresh: boolean;
  expiresIn: number; // milliseconds
  errors: string[];
}

/**
 * Secure Token Storage Service
 */
export class SecureTokenStorage {
  private config: TokenStorageConfig;
  private isInitialized = false;
  private isNative = false;
  private encryptionKey: CryptoKey | null = null;

  // Storage keys
  private readonly TOKENS_KEY = 'secure_tokens';
  private readonly ENCRYPTION_SALT_KEY = 'encryption_salt';
  private readonly TOKEN_METADATA_KEY = 'token_metadata';

  constructor(config: Partial<TokenStorageConfig> = {}) {
    this.config = {
      enableEncryption: true,
      encryptionAlgorithm: 'AES-GCM',
      keyDerivationIterations: 100000,
      keyPrefix: 'matmonster_',
      tokenExpirationBuffer: 300000, // 5 minutes
      enableAutoCleanup: true,
      maxTokenAge: 2592000000, // 30 days
      enableTokenValidation: true,
      enableWebFallback: true,
      webStorageType: 'localStorage',
      enableDebugLogging: false,
      ...config
    };

    this.isNative = isNativeApp();
  }

  /**
   * Initialize the secure token storage
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      if (this.config.enableDebugLogging) {
        console.log('🔐 Initializing Secure Token Storage...');
      }

      // Initialize encryption if enabled
      if (this.config.enableEncryption) {
        await this.initializeEncryption();
      }

      // Perform cleanup if enabled
      if (this.config.enableAutoCleanup) {
        await this.performCleanup();
      }

      this.isInitialized = true;

      if (this.config.enableDebugLogging) {
        console.log('✅ Secure Token Storage initialized successfully');
      }
    } catch (error) {
      console.error('❌ Failed to initialize Secure Token Storage:', error);
      throw error;
    }
  }

  /**
   * Store tokens securely
   */
  async storeTokens(tokenData: StoredTokenData): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // Add metadata
      const enrichedTokenData: StoredTokenData = {
        ...tokenData,
        createdAt: tokenData.createdAt || new Date().toISOString(),
        lastRefreshed: new Date().toISOString()
      };

      // Validate token data
      if (this.config.enableTokenValidation) {
        this.validateTokenData(enrichedTokenData);
      }

      // Encrypt and store
      if (this.config.enableEncryption && this.encryptionKey) {
        const encryptedData = await this.encryptData(JSON.stringify(enrichedTokenData));
        await this.setSecureValue(this.TOKENS_KEY, encryptedData);
      } else {
        await this.setSecureValue(this.TOKENS_KEY, JSON.stringify(enrichedTokenData));
      }

      // Store metadata separately
      const metadata = {
        storedAt: new Date().toISOString(),
        expiresAt: enrichedTokenData.expiresAt,
        userId: enrichedTokenData.userId,
        encrypted: this.config.enableEncryption && !!this.encryptionKey
      };

      await this.setSecureValue(this.TOKEN_METADATA_KEY, JSON.stringify(metadata));

      if (this.config.enableDebugLogging) {
        console.log('🔐 Tokens stored securely');
      }
    } catch (error) {
      console.error('❌ Failed to store tokens:', error);
      throw error;
    }
  }

  /**
   * Retrieve tokens securely
   */
  async retrieveTokens(): Promise<StoredTokenData | null> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const storedData = await this.getSecureValue(this.TOKENS_KEY);
      
      if (!storedData) {
        return null;
      }

      let tokenData: StoredTokenData;

      // Decrypt if necessary
      if (this.config.enableEncryption && this.encryptionKey) {
        const decryptedData = await this.decryptData(storedData);
        tokenData = JSON.parse(decryptedData);
      } else {
        tokenData = JSON.parse(storedData);
      }

      // Validate retrieved tokens
      if (this.config.enableTokenValidation) {
        const validation = this.validateStoredTokens(tokenData);
        if (!validation.isValid) {
          if (this.config.enableDebugLogging) {
            console.warn('🔐 Retrieved tokens are invalid:', validation.errors);
          }
          await this.clearTokens();
          return null;
        }
      }

      if (this.config.enableDebugLogging) {
        console.log('🔐 Tokens retrieved successfully');
      }

      return tokenData;
    } catch (error) {
      console.error('❌ Failed to retrieve tokens:', error);
      return null;
    }
  }

  /**
   * Update access token (for token refresh)
   */
  async updateAccessToken(accessToken: string, expiresAt: string): Promise<void> {
    const existingTokens = await this.retrieveTokens();
    
    if (!existingTokens) {
      throw new Error('No existing tokens found to update');
    }

    const updatedTokens: StoredTokenData = {
      ...existingTokens,
      accessToken,
      expiresAt,
      lastRefreshed: new Date().toISOString()
    };

    await this.storeTokens(updatedTokens);
  }

  /**
   * Check if tokens exist
   */
  async hasTokens(): Promise<boolean> {
    try {
      const tokens = await this.retrieveTokens();
      return tokens !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Validate stored tokens
   */
  async validateTokens(): Promise<TokenValidationResult> {
    const tokens = await this.retrieveTokens();
    
    if (!tokens) {
      return {
        isValid: false,
        isExpired: true,
        needsRefresh: false,
        expiresIn: 0,
        errors: ['No tokens found']
      };
    }

    return this.validateStoredTokens(tokens);
  }

  /**
   * Clear all stored tokens
   */
  async clearTokens(): Promise<void> {
    try {
      await this.removeSecureValue(this.TOKENS_KEY);
      await this.removeSecureValue(this.TOKEN_METADATA_KEY);

      if (this.config.enableDebugLogging) {
        console.log('🔐 Tokens cleared successfully');
      }
    } catch (error) {
      console.error('❌ Failed to clear tokens:', error);
      throw error;
    }
  }

  /**
   * Get token metadata
   */
  async getTokenMetadata(): Promise<Record<string, unknown> | null> {
    try {
      const metadataStr = await this.getSecureValue(this.TOKEN_METADATA_KEY);
      return metadataStr ? JSON.parse(metadataStr) : null;
    } catch (error) {
      console.error('Failed to get token metadata:', error);
      return null;
    }
  }

  /**
   * Initialize encryption
   */
  private async initializeEncryption(): Promise<void> {
    try {
      // Get or create encryption salt
      let salt = await this.getSecureValue(this.ENCRYPTION_SALT_KEY);
      
      if (!salt) {
        // Generate new salt
        const saltArray = crypto.getRandomValues(new Uint8Array(32));
        salt = Array.from(saltArray).map(b => b.toString(16).padStart(2, '0')).join('');
        await this.setSecureValue(this.ENCRYPTION_SALT_KEY, salt);
      }

      // Derive encryption key
      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode('matmonster-secure-storage'),
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey']
      );

      this.encryptionKey = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: new Uint8Array(salt.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16))),
          iterations: this.config.keyDerivationIterations,
          hash: 'SHA-256'
        },
        keyMaterial,
        { name: this.config.encryptionAlgorithm, length: 256 },
        false,
        ['encrypt', 'decrypt']
      );

      if (this.config.enableDebugLogging) {
        console.log('🔐 Encryption initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize encryption:', error);
      throw error;
    }
  }

  /**
   * Encrypt data
   */
  private async encryptData(data: string): Promise<string> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not initialized');
    }

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encodedData = new TextEncoder().encode(data);

    const encryptedData = await crypto.subtle.encrypt(
      { name: this.config.encryptionAlgorithm, iv },
      this.encryptionKey,
      encodedData
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedData), iv.length);

    // Convert to base64
    return btoa(String.fromCharCode(...combined));
  }

  /**
   * Decrypt data
   */
  private async decryptData(encryptedData: string): Promise<string> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not initialized');
    }

    // Convert from base64
    const combined = new Uint8Array(
      atob(encryptedData).split('').map(char => char.charCodeAt(0))
    );

    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const data = combined.slice(12);

    const decryptedData = await crypto.subtle.decrypt(
      { name: this.config.encryptionAlgorithm, iv },
      this.encryptionKey,
      data
    );

    return new TextDecoder().decode(decryptedData);
  }

  /**
   * Set secure value (platform-specific)
   */
  private async setSecureValue(key: string, value: string): Promise<void> {
    const fullKey = this.config.keyPrefix + key;

    if (this.isNative) {
      await Preferences.set({ key: fullKey, value });
    } else if (this.config.enableWebFallback) {
      if (this.config.webStorageType === 'localStorage') {
        localStorage.setItem(fullKey, value);
      } else {
        sessionStorage.setItem(fullKey, value);
      }
    } else {
      throw new Error('Secure storage not available on this platform');
    }
  }

  /**
   * Get secure value (platform-specific)
   */
  private async getSecureValue(key: string): Promise<string | null> {
    const fullKey = this.config.keyPrefix + key;

    if (this.isNative) {
      const result = await Preferences.get({ key: fullKey });
      return result.value;
    } else if (this.config.enableWebFallback) {
      if (this.config.webStorageType === 'localStorage') {
        return localStorage.getItem(fullKey);
      } else {
        return sessionStorage.getItem(fullKey);
      }
    } else {
      throw new Error('Secure storage not available on this platform');
    }
  }

  /**
   * Remove secure value (platform-specific)
   */
  private async removeSecureValue(key: string): Promise<void> {
    const fullKey = this.config.keyPrefix + key;

    if (this.isNative) {
      await Preferences.remove({ key: fullKey });
    } else if (this.config.enableWebFallback) {
      if (this.config.webStorageType === 'localStorage') {
        localStorage.removeItem(fullKey);
      } else {
        sessionStorage.removeItem(fullKey);
      }
    } else {
      throw new Error('Secure storage not available on this platform');
    }
  }

  /**
   * Validate token data structure
   */
  private validateTokenData(tokenData: StoredTokenData): void {
    const required = ['accessToken', 'refreshToken', 'expiresAt', 'tokenType'];
    const missing = required.filter(field => !tokenData[field as keyof StoredTokenData]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required token fields: ${missing.join(', ')}`);
    }

    // Validate expiration date
    try {
      new Date(tokenData.expiresAt);
    } catch (error) {
      throw new Error('Invalid expiration date format');
    }
  }

  /**
   * Validate stored tokens
   */
  private validateStoredTokens(tokens: StoredTokenData): TokenValidationResult {
    const errors: string[] = [];
    const now = Date.now();
    const expiresAt = new Date(tokens.expiresAt).getTime();
    const expiresIn = expiresAt - now;
    
    // Check if expired
    const isExpired = expiresIn <= 0;
    if (isExpired) {
      errors.push('Token is expired');
    }

    // Check if needs refresh (within buffer time)
    const needsRefresh = expiresIn <= this.config.tokenExpirationBuffer;
    if (needsRefresh && !isExpired) {
      errors.push('Token needs refresh');
    }

    // Check token age
    if (tokens.createdAt) {
      const tokenAge = now - new Date(tokens.createdAt).getTime();
      if (tokenAge > this.config.maxTokenAge) {
        errors.push('Token is too old');
      }
    }

    // Check required fields
    if (!tokens.accessToken || !tokens.refreshToken) {
      errors.push('Missing required token fields');
    }

    const isValid = errors.length === 0 || (errors.length === 1 && errors[0] === 'Token needs refresh');

    return {
      isValid,
      isExpired,
      needsRefresh,
      expiresIn: Math.max(0, expiresIn),
      errors
    };
  }

  /**
   * Perform cleanup of old/invalid tokens
   */
  private async performCleanup(): Promise<void> {
    try {
      const validation = await this.validateTokens();
      
      if (!validation.isValid && validation.isExpired) {
        await this.clearTokens();
        
        if (this.config.enableDebugLogging) {
          console.log('🔐 Cleaned up expired tokens');
        }
      }
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.warn('Failed to perform token cleanup:', error);
      }
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): TokenStorageConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<TokenStorageConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Check if running on native platform
   */
  isNativePlatform(): boolean {
    return this.isNative;
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    hasTokens: boolean;
    isEncrypted: boolean;
    platform: 'native' | 'web';
    lastStored?: string;
    expiresAt?: string;
  }> {
    const metadata = await this.getTokenMetadata();
    const hasTokens = await this.hasTokens();

    return {
      hasTokens,
      isEncrypted: this.config.enableEncryption && !!this.encryptionKey,
      platform: this.isNative ? 'native' : 'web',
      lastStored: metadata?.storedAt as string,
      expiresAt: metadata?.expiresAt as string
    };
  }
}

// Export singleton instance
export const secureTokenStorage = new SecureTokenStorage();
